import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { isLiquorModuleEnabled } from '../config/featureFlags';
import { apiFetch } from '../utils/api';
import { centralToday } from '../utils/centralDate';
import {
  FACILITY_SPIRITS_DSP_SITES,
  FacilitySpiritContainer,
  isSpiritContainerEmpty,
  productsCompatibleWithContainer,
  sameSpiritId,
  uniqueCompatibleProductId,
} from '../utils/facilitySpirits';
import { bottlingTankSummaryReportIds } from '../utils/spiritsHistory';
import './spirits/SpiritsPrint.css';

type DspSite = { siteId: string; name?: string };
type Product = {
  id: number;
  code: string;
  name: string;
  kind: string;
  targetProof: number;
  bottlesPerCase: number;
  bottleSizeMl: number;
  batchCode?: string | null;
  formulaNumber?: string | null;
  sourceCategory?: string | null;
};
type Source = {
  id: number;
  serial: string;
  containerType: string;
  kind: string;
  proof: number;
  remainingProofGallons: number;
  producerPlantNumber?: string;
  currentGrossLb?: number | null;
  tareLb?: number;
};
type Plan = {
  pumpSpiritPounds: number;
  addWaterPounds: number;
  spiritWineGallons: number;
  waterAdjustedWg: number;
  totalVolumeWg: number;
  proofGallons: number;
  totalWeightLb: number;
  cases: number;
  /** Implied volume contraction — information only (27 CFR 30.64 water already includes it). */
  shrinkPct: number;
  waterMethod?: string;
  volumeMethodCheck?: { waterWineGallons: number; waterPounds: number; source: string };
  scaleStopLb?: number | null;
  spiritPounds: number;
  waterPounds: number;
};

const PERJURY =
  'Under penalties of perjury, I declare that I have examined this report, and to the best of my knowledge and belief, it is true, correct, and complete, and is supported by true, correct, and complete commercial records which are available for inspection.';

type Mode = 'home' | 'bottle' | 'transfer' | 'containers' | 'history' | 'print' | 'products' | 'edit';

const MODE_TITLES: Record<Exclude<Mode, 'home'>, string> = {
  bottle: 'Bottling',
  transfer: 'Bond-to-bond transfer',
  containers: 'Containers / receive',
  history: 'History / reprint',
  products: 'Product batch codes',
  print: 'Print',
  edit: 'Edit',
};

const WIZARD_STEPS = ['Product', 'Source', 'Mix plan', 'Actuals', 'Save'] as const;

type PendingReturn = { mode: 'bottle'; step: number };

const SpiritsDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const deepLinkAppliedRef = useRef(false);
  const [sites, setSites] = useState<DspSite[]>([]);
  const [siteId, setSiteId] = useState('DSP-AL-20010');
  const [products, setProducts] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('home');
  const [printDocs, setPrintDocs] = useState<any[]>([]);
  /** After receive-from-wizard empty state, return here (and after print Done). */
  const [pendingReturn, setPendingReturn] = useState<PendingReturn | null>(null);
  /** Facility View / deep-link: filter History to one container. */
  const [historyContainerId, setHistoryContainerId] = useState<number | null>(null);
  const [containerHistory, setContainerHistory] = useState<any>(null);
  /** Facility View Bottle deep-link: exact drum/barrel to keep selected through the wizard. */
  const [bottlePrefillContainer, setBottlePrefillContainer] = useState<FacilitySpiritContainer | null>(
    null
  );
  const bottlePrefillIdRef = useRef<number | null>(null);

  // Bottling wizard
  const [step, setStep] = useState(1);
  const [productId, setProductId] = useState('');
  const [bottles, setBottles] = useState('84');
  const [bottleDate, setBottleDate] = useState(centralToday());
  const [dumpBatchId, setDumpBatchId] = useState('');
  /** Past bottling whose bottles are already sold: withdraw from the drum only. */
  const [alreadySold, setAlreadySold] = useState(false);
  const [sources, setSources] = useState<Source[]>([]);
  const [selectedSourceIds, setSelectedSourceIds] = useState<number[]>([]);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [actuals, setActuals] = useState({
    startGrossLb: '',
    stopGrossLb: '',
    /** Actual water added at the scale (lb). Blank = not recorded; planned water is used. */
    actualWaterPounds: '',
    labelingProof: '',
    temperatureF: '68',
    bottlesFilled: '',
    tankNumber: 'BT1',
    formulaNumber: 'N/A',
    notes: '',
  });

  // Transfer
  const [containers, setContainers] = useState<any[]>([]);
  const [ship, setShip] = useState({
    fromContainerId: '',
    proofGallons: '',
    transferNumber: '',
    transferredAt: centralToday(),
    toSiteId: 'DSP-AL-20051',
  });
  const [receive, setReceive] = useState({
    transferId: '',
    toContainerSerial: '',
    proofGallonsReceived: '',
    receivedAt: centralToday(),
  });
  const [history, setHistory] = useState<any>(null);
  const [editingBatchId, setEditingBatchId] = useState<number | null>(null);
  const [editingDrumId, setEditingDrumId] = useState<number | null>(null);

  // Receive setup
  const [drumForm, setDrumForm] = useState({
    serial: '',
    kind: 'Neutral Spirits',
    proof: '190',
    proofGallons: '',
    tareLb: '20',
    currentGrossLb: '',
    vendorName: '',
    producerPlantNumber: '',
    receivedAt: centralToday(),
  });
  const [barrelForm, setBarrelForm] = useState({
    serial: '',
    kind: 'Bourbon',
    capacityGal: '35',
    entryProof: '125',
    fillDate: '',
    producerPlantNumber: 'DSP-AL-20051',
  });

  const product = useMemo(
    () => products.find((p) => String(p.id) === String(productId)),
    [products, productId]
  );

  /** When Bottle was opened from a Facility tile, only offer products that can use that spirit. */
  const bottleProductChoices = useMemo(() => {
    if (!bottlePrefillContainer) return products;
    const matched = productsCompatibleWithContainer(bottlePrefillContainer, products);
    return matched.length ? matched : products;
  }, [bottlePrefillContainer, products]);

  /** Transfer ship picker: exclude destroyed / empty containers. */
  const transferableContainers = useMemo(
    () => (containers || []).filter((c) => !isSpiritContainerEmpty(c)),
    [containers]
  );

  const onHandContainers = useMemo(
    () => (containers || []).filter((c) => !isSpiritContainerEmpty(c)),
    [containers]
  );

  const postJson = async (path: string, body: object) => {
    const res = await apiFetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  };

  const patchJson = async (path: string, body: object) => {
    const res = await apiFetch(`${API_BASE_URL}${path}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  };

  const openBottlingEdit = (batchId: number) => {
    setEditingBatchId(batchId);
    setEditingDrumId(null);
    setMode('edit');
    setError(null);
  };

  const loadBase = useCallback(async () => {
    if (!isLiquorModuleEnabled()) return;
    setError(null);
    const [sitesRes, prodRes, contRes, histRes] = await Promise.all([
      apiFetch(`${API_BASE_URL}/api/liquor/sites`),
      apiFetch(`${API_BASE_URL}/api/liquor/products`),
      apiFetch(`${API_BASE_URL}/api/liquor/containers?siteId=${encodeURIComponent(siteId)}`),
      apiFetch(`${API_BASE_URL}/api/liquor/history?siteId=${encodeURIComponent(siteId)}`),
    ]);
    if (!sitesRes.ok || !prodRes.ok || !contRes.ok || !histRes.ok) {
      throw new Error('Could not load spirits data');
    }
    setSites(await sitesRes.json());
    setProducts(await prodRes.json());
    setContainers(await contRes.json());
    setHistory(await histRes.json());
  }, [siteId]);

  useEffect(() => {
    loadBase().catch((e) => setError(e.message));
  }, [loadBase]);

  // Facility View deep-links: /spirits?siteId=&mode=bottle|transfer|history&containerId=
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const modeParam = searchParams.get('mode');
    const siteParam = searchParams.get('siteId');
    const containerParam = searchParams.get('containerId');
    if (!modeParam && !siteParam && !containerParam) return;
    deepLinkAppliedRef.current = true;

    const nextSite =
      siteParam && (FACILITY_SPIRITS_DSP_SITES as readonly string[]).includes(siteParam)
        ? siteParam
        : null;
    if (nextSite) setSiteId(nextSite);

    const cid = containerParam != null && containerParam !== '' ? Number(containerParam) : NaN;
    const hasContainer = Number.isFinite(cid) && cid > 0;

    if (modeParam === 'bottle') {
      setPendingReturn(null);
      setMode('bottle');
      setStep(1);
      setPlan(null);
      setSources([]);
      setAlreadySold(false);
      setProductId('');
      setBottlePrefillContainer(null);
      bottlePrefillIdRef.current = hasContainer ? cid : null;
      setSelectedSourceIds(hasContainer ? [cid] : []);
      setError(null);
      if (hasContainer) {
        // Load the exact drum/barrel so kind (cane neutral vs rum) drives product choices.
        void (async () => {
          try {
            const res = await apiFetch(`${API_BASE_URL}/api/liquor/containers/${cid}`);
            if (!res.ok) return;
            const row = await res.json();
            if (bottlePrefillIdRef.current !== cid) return;
            setBottlePrefillContainer(row);
            setSelectedSourceIds((ids) =>
              ids.some((id) => sameSpiritId(id, cid)) ? ids : [cid, ...ids]
            );
          } catch {
            /* keep id selection; banner stays empty until containers list loads */
          }
        })();
      }
    } else if (modeParam === 'transfer') {
      setPendingReturn(null);
      setMode('transfer');
      setError(null);
      const fromSite = nextSite || siteId;
      setShip((s) => ({
        ...s,
        fromContainerId: hasContainer ? String(cid) : s.fromContainerId,
        toSiteId: fromSite === 'DSP-AL-20010' ? 'DSP-AL-20051' : 'DSP-AL-20010',
      }));
    } else if (modeParam === 'history') {
      setPendingReturn(null);
      setMode('history');
      setError(null);
      if (hasContainer) setHistoryContainerId(cid);
    }

    setSearchParams({}, { replace: true });
    // Only consume the landing query once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  // Once products + prefilled container are known, auto-pick product only when unique.
  useEffect(() => {
    if (!bottlePrefillContainer || !products.length) return;
    const onlyId = uniqueCompatibleProductId(bottlePrefillContainer, products);
    if (onlyId != null) {
      setProductId(String(onlyId));
    }
  }, [bottlePrefillContainer, products]);

  // If deep-link fetch failed, recover container row from the site containers list.
  useEffect(() => {
    const prefId = bottlePrefillIdRef.current;
    if (prefId == null || bottlePrefillContainer || !containers.length) return;
    const row = containers.find((c) => sameSpiritId(c.id, prefId));
    if (row) setBottlePrefillContainer(row);
  }, [containers, bottlePrefillContainer]);

  // Container-scoped history for Facility View deep-link / filtered history.
  useEffect(() => {
    if (mode !== 'history' || historyContainerId == null) {
      setContainerHistory(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/liquor/containers/${historyContainerId}/history`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) setContainerHistory(data);
      } catch (e: any) {
        if (!cancelled) setError(e.message || 'Could not load container history');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, historyContainerId]);

  // Prefill dump/batch ID when product + date change
  useEffect(() => {
    if (!product?.batchCode || !bottleDate) return;
    let cancelled = false;
    (async () => {
      const q = new URLSearchParams({
        batchCode: product.batchCode!,
        date: bottleDate,
        siteId,
      });
      const res = await apiFetch(`${API_BASE_URL}/api/liquor/bottling/suggest-id?${q}`);
      if (!res.ok || cancelled) return;
      const data = await res.json();
      if (!cancelled) setDumpBatchId(data.dumpBatchId);
    })();
    return () => {
      cancelled = true;
    };
  }, [product?.batchCode, bottleDate, siteId]);

  const clearBottlePrefill = () => {
    bottlePrefillIdRef.current = null;
    setBottlePrefillContainer(null);
  };

  const goHome = () => {
    setPendingReturn(null);
    setHistoryContainerId(null);
    setContainerHistory(null);
    clearBottlePrefill();
    setMode('home');
    setError(null);
  };

  const goToMode = (next: Mode) => {
    if (next === 'home') {
      goHome();
      return;
    }
    if (next !== 'containers' && next !== 'print') {
      setPendingReturn(null);
    }
    if (next !== 'history') {
      setHistoryContainerId(null);
      setContainerHistory(null);
    }
    setMode(next);
    setError(null);
  };

  const historyView = historyContainerId != null ? containerHistory : history;
  const historySerial =
    historyContainerId != null
      ? containerHistory?.container?.containerSerial ||
        containers.find((c) => Number(c.id) === Number(historyContainerId))?.containerSerial ||
        `#${historyContainerId}`
      : null;

  const finishPrintOrDone = async () => {
    if (pendingReturn?.mode === 'bottle') {
      const returnStep = pendingReturn.step;
      setPendingReturn(null);
      setMode('bottle');
      setStep(returnStep);
      setError(null);
      try {
        await loadSources();
      } catch (e: any) {
        setError(e.message);
      }
      return;
    }
    goHome();
  };

  const openReceiveForWizard = () => {
    setPendingReturn({ mode: 'bottle', step: 2 });
    setMode('containers');
    setError(null);
  };

  const startBottling = () => {
    setPendingReturn(null);
    clearBottlePrefill();
    setMode('bottle');
    setStep(1);
    setPlan(null);
    setProductId('');
    setSelectedSourceIds([]);
    setSources([]);
    setAlreadySold(false);
    setError(null);
  };

  const goWizardStep = (n: number) => {
    if (n < 1 || n > 5 || n >= step) return;
    setStep(n);
    setError(null);
  };

  const loadSources = async () => {
    if (!product) return;
    const q = new URLSearchParams({
      siteId,
      productCode: product.code,
      targetProof: String(product.targetProof),
    });
    const res = await apiFetch(`${API_BASE_URL}/api/liquor/sources?${q}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Could not load sources');
    const list: Source[] = Array.isArray(data) ? data : [];
    const prefId = bottlePrefillIdRef.current;
    const pref = bottlePrefillContainer;
    // Always keep the Facility-tapped container visible/selected, even when the
    // sources API omits it (kind/proof filter edge cases for 19.593 neutrals).
    if (prefId != null && pref && !list.some((s) => sameSpiritId(s.id, prefId))) {
      list.unshift({
        id: Number(pref.id),
        serial: pref.containerSerial,
        containerType: String(pref.containerType || 'Drum'),
        kind: String(pref.kind || ''),
        proof: Number(pref.proof) || 0,
        remainingProofGallons: Number(pref.remainingProofGallons) || 0,
        producerPlantNumber: pref.producerPlantNumber || undefined,
        currentGrossLb: (pref as any).currentGrossLb ?? null,
        tareLb: (pref as any).tareLb,
      });
    }
    setSources(list);
    if (prefId != null) {
      setSelectedSourceIds((ids) => {
        const kept = ids.filter((id) => list.some((s) => sameSpiritId(s.id, id)));
        if (kept.some((id) => sameSpiritId(id, prefId))) return kept.length ? kept : [prefId];
        return [prefId, ...kept];
      });
    }
  };

  const buildPlan = async () => {
    if (!product || selectedSourceIds.length === 0) return;
    const primary = sources.find((s) => sameSpiritId(s.id, selectedSourceIds[0]));
    if (!primary) return;
    const body: any = {
      bottles: Number(bottles),
      bottleSizeMl: product.bottleSizeMl || 750,
      bottlesPerCase: product.bottlesPerCase,
      sourceProof: primary.proof,
      targetProof: product.targetProof,
    };
    if (primary.currentGrossLb != null) body.currentGrossLb = primary.currentGrossLb;
    const data = await postJson('/api/liquor/bottling/plan', body);
    setPlan(data);
    setActuals((a) => ({
      ...a,
      labelingProof: String(product.targetProof),
      bottlesFilled: bottles,
      actualWaterPounds: '',
      formulaNumber: product.formulaNumber || 'N/A',
      startGrossLb: primary.currentGrossLb != null ? String(primary.currentGrossLb) : a.startGrossLb,
      stopGrossLb: data.scaleStopLb != null ? String(data.scaleStopLb) : a.stopGrossLb,
    }));
  };

  const saveBottling = async () => {
    if (!product || !plan || selectedSourceIds.length === 0) return;
    const inputs = selectedSourceIds.map((id, idx) => {
      const src = sources.find((s) => sameSpiritId(s.id, id))!;
      // Primary source uses scale weights; additional sources need PG or weights later
      if (idx === 0 && actuals.startGrossLb && actuals.stopGrossLb) {
        return {
          containerId: src.id,
          startGrossLb: Number(actuals.startGrossLb),
          stopGrossLb: Number(actuals.stopGrossLb),
        };
      }
      return {
        containerId: src.id,
        proofGallons: plan.proofGallons / selectedSourceIds.length,
      };
    });
    const result = await postJson('/api/liquor/bottling', {
      siteId,
      productId: product.id,
      productCode: product.code,
      productName: product.name,
      batchCode: product.batchCode,
      typeOfSpirits: product.kind,
      dumpBatchId: dumpBatchId || undefined,
      bottlingTankNumber: actuals.tankNumber,
      formulaNumber: actuals.formulaNumber,
      bottledAt: bottleDate,
      temperatureF: Number(actuals.temperatureF),
      labelingProof: Number(actuals.labelingProof),
      bottleSizeMl: product.bottleSizeMl || 750,
      bottlesFilled: Number(actuals.bottlesFilled || bottles),
      bottlesPerCase: product.bottlesPerCase,
      plannedWaterPounds: plan.addWaterPounds,
      actualWaterPounds: actuals.actualWaterPounds.trim() === '' ? null : Number(actuals.actualWaterPounds),
      waterPounds:
        actuals.actualWaterPounds.trim() === '' ? plan.addWaterPounds : Number(actuals.actualWaterPounds),
      shrinkPct: plan.shrinkPct,
      plan,
      notes: actuals.notes,
      addFinishedGoods: !alreadySold,
      inputs,
    });
    const docs = [result.dumpBatchSheet, ...(result.tankSummaryReports || [])];
    setPrintDocs(docs);
    setMode('print');
    await loadBase();
  };

  if (!isLiquorModuleEnabled()) {
    return (
      <div className="page-container container">
        <h2 className="app-header mb-4">Spirits</h2>
        <p>
          Spirits module is off. Set <code>REACT_APP_LIQUOR_MODULE=1</code> and rebuild; server needs{' '}
          <code>LIQUOR_MODULE=1</code>.
        </p>
      </div>
    );
  }

  return (
    <div className="page-container container spirits-page" data-testid="spirits-dashboard">
      <div className="no-print spirits-topbar">
        <h2 className="app-header spirits-heading mb-0">
          {mode === 'home' ? (
            'Spirits'
          ) : (
            <span className="spirits-breadcrumb">
              <button type="button" className="spirits-crumb-link" onClick={goHome} data-testid="spirits-home-crumb">
                Spirits
              </button>
              <span className="spirits-crumb-sep" aria-hidden="true">
                ›
              </span>
              <span className="spirits-crumb-current">
                {mode === 'edit' && editingDrumId != null && editingBatchId == null
                  ? 'Edit drum receipt'
                  : mode === 'edit'
                    ? 'Edit bottling'
                    : MODE_TITLES[mode]}
              </span>
            </span>
          )}
        </h2>
        <label className="spirits-site-label mb-0">
          Distillery{' '}
          <select
            className="form-select form-select-sm d-inline-block w-auto"
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            data-testid="spirits-site"
          >
            {(sites.length ? sites : [{ siteId: 'DSP-AL-20010' }, { siteId: 'DSP-AL-20051' }]).map(
              (s) => (
                <option key={s.siteId} value={s.siteId}>
                  {s.name ? `${s.name}` : s.siteId}
                </option>
              )
            )}
          </select>
        </label>
      </div>

      {error && (
        <div className="alert alert-danger no-print" role="alert">
          {error}
        </div>
      )}

      {mode === 'home' && (
        <div className="no-print">
          <div className="spirits-hero-actions">
            <button type="button" className="spirits-hero-btn" onClick={startBottling} data-testid="start-bottling">
              Bottling
              <small>Proof down and fill bottles. Creates the dump/batch sheet and tank reports automatically.</small>
            </button>
            <button
              type="button"
              className="spirits-hero-btn"
              onClick={() => {
                goToMode('transfer');
                setShip((s) => ({
                  ...s,
                  toSiteId: siteId === 'DSP-AL-20010' ? 'DSP-AL-20051' : 'DSP-AL-20010',
                }));
              }}
              data-testid="start-transfer"
            >
              Bond-to-bond transfer
              <small>Move bulk spirit between Madison and Athens. Bottled goods stay put.</small>
            </button>
          </div>
          <div className="spirits-secondary-actions">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => goToMode('containers')}>
              Containers / receive
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => goToMode('history')}>
              History / reprint
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => goToMode('products')}>
              Product batch codes
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate(`/spirits/bottles?siteId=${encodeURIComponent(siteId)}`)}
              data-testid="spirits-bottles-on-hand"
            >
              Bottles on hand
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate(`/spirits/bottle-counts?siteId=${encodeURIComponent(siteId)}`)}
              data-testid="spirits-bottle-counts"
            >
              Bottle counts
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate(`/spirits/taproom-orders?siteId=${encodeURIComponent(siteId)}`)}
              data-testid="spirits-taproom-orders"
            >
              Taproom orders
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate(`/spirits/taproom-report?siteId=${encodeURIComponent(siteId)}`)}
              data-testid="spirits-taproom-report"
            >
              Liquor report
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate(`/spirits/ttb-reports?siteId=${encodeURIComponent(siteId)}`)}
              data-testid="spirits-ttb-reports"
            >
              TTB monthly reports
            </button>
          </div>
          <div className="mt-4">
            <h3 className="h6">On hand at this distillery</h3>
            {onHandContainers.length === 0 ? (
              <p className="text-muted mb-0">No drums or barrels with spirit left — use Containers / receive.</p>
            ) : (
              <ul className="spirits-onhand-list list-unstyled mb-0">
                {onHandContainers.map((c) => (
                  <li key={c.id} className="spirits-onhand-card">
                    <div className="spirits-onhand-title">
                      <strong>{c.containerSerial}</strong>
                      <span className="text-muted"> · {c.containerType}</span>
                    </div>
                    <div className="small">
                      {c.kind} @ {c.proof}° · {Number(c.remainingProofGallons).toFixed(2)} PG
                    </div>
                    <div className="small text-muted">Producer {c.producerPlantNumber || '—'}</div>
                    {c.containerType === 'Drum' && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm mt-1"
                        data-testid={`edit-drum-receipt-${c.id}`}
                        onClick={() => {
                          setEditingDrumId(c.id);
                          setEditingBatchId(null);
                          setMode('edit');
                          setError(null);
                        }}
                      >
                        Edit receipt
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {mode === 'bottle' && (
        <div className="no-print spirits-subscreen">
          <div className="spirits-stepper" role="navigation" aria-label="Bottling steps">
            {WIZARD_STEPS.map((label, idx) => {
              const n = idx + 1;
              const status = step === n ? 'active' : step > n ? 'done' : 'todo';
              const canJump = n < step;
              return (
                <button
                  key={n}
                  type="button"
                  className={`spirits-step ${status}`}
                  disabled={!canJump}
                  aria-current={step === n ? 'step' : undefined}
                  onClick={() => goWizardStep(n)}
                >
                  {n}. {label}
                </button>
              );
            })}
          </div>

          {step === 1 && (
            <div>
              <p className="text-muted">Pick what you are bottling and how many bottles.</p>
              {bottlePrefillContainer && (
                <div
                  className="spirits-return-banner"
                  role="status"
                  data-testid="spirits-bottle-prefill"
                >
                  Source from Facility View:{' '}
                  <strong data-testid="spirits-bottle-prefill-serial">
                    {bottlePrefillContainer.containerSerial}
                  </strong>
                  <span className="ms-1">
                    · {bottlePrefillContainer.kind || 'Spirit'} @{' '}
                    {bottlePrefillContainer.proof != null
                      ? `${Number(bottlePrefillContainer.proof)}°`
                      : '—'}{' '}
                    · {Number(bottlePrefillContainer.remainingProofGallons || 0).toFixed(2)} PG
                  </span>
                  {bottleProductChoices.length > 1 && (
                    <div className="small mt-1">
                      Several products can use this spirit — pick which one you are bottling.
                    </div>
                  )}
                </div>
              )}
              <div className="spirits-field">
                <label htmlFor="spirit-product">Product</label>
                <select
                  id="spirit-product"
                  className="form-select"
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  data-testid="spirit-product"
                >
                  <option value="">Select…</option>
                  {bottleProductChoices.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} (target {p.targetProof}°, {p.bottlesPerCase}/case)
                    </option>
                  ))}
                </select>
              </div>
              <div className="spirits-field">
                <label htmlFor="spirit-bottles">Number of bottles</label>
                <input
                  id="spirit-bottles"
                  className="form-control"
                  inputMode="numeric"
                  value={bottles}
                  onChange={(e) => setBottles(e.target.value)}
                />
                <span className="hint">Usually 750 ml. Case size comes from the product.</span>
              </div>
              <div className="spirits-field">
                <label htmlFor="spirit-date">Bottling date</label>
                <input
                  id="spirit-date"
                  type="date"
                  className="form-control"
                  value={bottleDate}
                  onChange={(e) => setBottleDate(e.target.value)}
                />
              </div>
              <div className="spirits-field">
                <label htmlFor="spirit-batch-id">Dump / Batch ID</label>
                <input
                  id="spirit-batch-id"
                  className="form-control"
                  value={dumpBatchId}
                  onChange={(e) => setDumpBatchId(e.target.value.toUpperCase())}
                  placeholder={product?.batchCode ? `${product.batchCode} + date` : 'Set product batch code first'}
                />
                <span className="hint">
                  Auto-filled as batch code + date (ddmmyy Central), e.g. SOM271026. One batch per product per day at
                  this distillery.
                </span>
              </div>
              <div className="spirits-wizard-nav">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!product || !Number(bottles)}
                  onClick={async () => {
                    try {
                      await loadSources();
                      setStep(2);
                    } catch (e: any) {
                      setError(e.message);
                    }
                  }}
                >
                  Next — pick source
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-muted">
                Stored liquor at this distillery that can make <strong>{product?.name}</strong> (proof ≥{' '}
                {product?.targetProof}°). Select one or more.
              </p>
              {bottlePrefillContainer && (
                <p className="small text-muted" data-testid="spirits-bottle-prefill-source-hint">
                  Prefilling source{' '}
                  <strong>{bottlePrefillContainer.containerSerial}</strong> (
                  {bottlePrefillContainer.kind || 'Spirit'}).
                </p>
              )}
              {sources.map((s) => {
                const selected = selectedSourceIds.some((id) => sameSpiritId(id, s.id));
                return (
                  <label
                    key={s.id}
                    className={`spirits-source-card ${selected ? 'selected' : ''}`}
                    style={{ display: 'block', cursor: 'pointer' }}
                    data-testid={`spirits-source-${s.id}`}
                  >
                    <input
                      type="checkbox"
                      className="me-2"
                      checked={selected}
                      data-testid={`spirits-source-check-${s.id}`}
                      onChange={() =>
                        setSelectedSourceIds((ids) =>
                          selected
                            ? ids.filter((x) => !sameSpiritId(x, s.id))
                            : [...ids, Number(s.id)]
                        )
                      }
                    />
                    <strong>{s.serial}</strong> — {s.kind} @ {s.proof}°
                    <div className="small text-muted">
                      Remaining {Number(s.remainingProofGallons).toFixed(2)} PG · Producer{' '}
                      {s.producerPlantNumber || '—'}
                      {s.currentGrossLb != null ? ` · Scale ${s.currentGrossLb} lb` : ''}
                    </div>
                  </label>
                );
              })}
              {!sources.length && (
                <div className="spirits-empty-state" data-testid="no-eligible-sources">
                  <p className="mb-2">No eligible containers. Receive a drum/barrel first.</p>
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={openReceiveForWizard}
                    data-testid="receive-from-wizard"
                  >
                    Receive drum/barrel
                  </button>
                </div>
              )}
              <div className="spirits-wizard-nav">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setStep(1)}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!selectedSourceIds.length}
                  onClick={async () => {
                    try {
                      await buildPlan();
                      setStep(3);
                    } catch (e: any) {
                      setError(e.message);
                    }
                  }}
                >
                  Next — mix plan
                </button>
              </div>
            </div>
          )}

          {step === 3 && plan && (
            <div>
              <p className="text-muted">Pump this much spirit, then add this much water.</p>
              <div className="spirits-plan-box" data-testid="bottling-plan">
                <dl>
                  <dt>Spirit to pump</dt>
                  <dd>{plan.pumpSpiritPounds} lb ({plan.spiritWineGallons} gal)</dd>
                  {plan.scaleStopLb != null && (
                    <>
                      <dt>Scale stop weight</dt>
                      <dd>{plan.scaleStopLb} lb</dd>
                    </>
                  )}
                  <dt>Water to add</dt>
                  <dd>
                    {plan.addWaterPounds} lb ({plan.waterAdjustedWg} gal)
                    <div className="hint">
                      27 CFR 30.64 (TTB Gauging Manual Table 4). Shrink {plan.shrinkPct}% — info only.
                      {plan.volumeMethodCheck
                        ? ` Table 6 volume check: ${plan.volumeMethodCheck.waterPounds} lb.`
                        : ''}
                    </div>
                  </dd>
                  <dt>Total volume</dt>
                  <dd>{plan.totalVolumeWg} gal</dd>
                  <dt>Proof gallons</dt>
                  <dd>{plan.proofGallons}</dd>
                  <dt>Total weight</dt>
                  <dd>{plan.totalWeightLb} lb</dd>
                  <dt>Cases (approx.)</dt>
                  <dd>{plan.cases}</dd>
                </dl>
              </div>
              <div className="spirits-wizard-nav">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setStep(2)}>
                  Back
                </button>
                <button type="button" className="btn btn-primary" onClick={() => setStep(4)}>
                  Next — enter actuals
                </button>
              </div>
            </div>
          )}

          {step === 4 && plan && (
            <div>
              <p className="text-muted">Enter what the scale and meter actually showed. Variance vs plan is below.</p>
              <div className="spirits-field">
                <label>Start weight on scale (lb)</label>
                <input
                  className="form-control"
                  inputMode="decimal"
                  value={actuals.startGrossLb}
                  onChange={(e) => setActuals({ ...actuals, startGrossLb: e.target.value })}
                />
              </div>
              <div className="spirits-field">
                <label>Stop weight on scale (lb)</label>
                <input
                  className="form-control"
                  inputMode="decimal"
                  value={actuals.stopGrossLb}
                  onChange={(e) => setActuals({ ...actuals, stopGrossLb: e.target.value })}
                />
                <span className="hint">
                  Plan: pump {plan.pumpSpiritPounds} lb
                  {plan.scaleStopLb != null ? ` → stop at ${plan.scaleStopLb} lb` : ''}.
                </span>
              </div>
              <div className="spirits-field">
                <label>Actual water added (lb)</label>
                <input
                  className="form-control"
                  inputMode="decimal"
                  data-testid="actual-water-pounds"
                  placeholder={`Planned ${plan.addWaterPounds}`}
                  value={actuals.actualWaterPounds}
                  onChange={(e) => setActuals({ ...actuals, actualWaterPounds: e.target.value })}
                />
                <span className="hint">
                  Planned: {plan.addWaterPounds} lb. Enter what the scale showed; leave blank if not weighed.
                </span>
              </div>
              <div className="spirits-field">
                <label>Final proof (Anton Paar)</label>
                <input
                  className="form-control"
                  inputMode="decimal"
                  value={actuals.labelingProof}
                  onChange={(e) => setActuals({ ...actuals, labelingProof: e.target.value })}
                />
              </div>
              <div className="spirits-field">
                <label>Temperature (°F)</label>
                <input
                  className="form-control"
                  inputMode="decimal"
                  value={actuals.temperatureF}
                  onChange={(e) => setActuals({ ...actuals, temperatureF: e.target.value })}
                />
              </div>
              <div className="spirits-field">
                <label>Bottles filled</label>
                <input
                  className="form-control"
                  inputMode="numeric"
                  value={actuals.bottlesFilled}
                  onChange={(e) => setActuals({ ...actuals, bottlesFilled: e.target.value })}
                />
              </div>
              <div className="spirits-field">
                <label>Bottling / mixing tank</label>
                <select
                  className="form-select"
                  value={actuals.tankNumber}
                  onChange={(e) => setActuals({ ...actuals, tankNumber: e.target.value })}
                >
                  <option value="BT1">BT1</option>
                  <option value="MT1">MT1</option>
                </select>
              </div>
              <div className="spirits-field">
                <label>Formula #</label>
                <input
                  className="form-control"
                  value={actuals.formulaNumber}
                  onChange={(e) => setActuals({ ...actuals, formulaNumber: e.target.value })}
                />
              </div>
              <div className="spirits-field">
                <label>Notes</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={actuals.notes}
                  onChange={(e) => setActuals({ ...actuals, notes: e.target.value })}
                />
              </div>
              {actuals.startGrossLb && actuals.stopGrossLb && (
                <p className="small">
                  Spirit pulled:{' '}
                  {(Number(actuals.startGrossLb) - Number(actuals.stopGrossLb)).toFixed(2)} lb vs plan{' '}
                  {plan.pumpSpiritPounds} lb (
                  {(
                    Number(actuals.startGrossLb) -
                    Number(actuals.stopGrossLb) -
                    plan.pumpSpiritPounds
                  ).toFixed(2)}{' '}
                  lb)
                </p>
              )}
              <div className="spirits-wizard-nav">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setStep(3)}>
                  Back
                </button>
                <button type="button" className="btn btn-primary" onClick={() => setStep(5)}>
                  Next — review &amp; save
                </button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <p>
                Saving will record the bottling, withdraw from the source container(s), and create the Dump/Batch Sheet
                plus Tank Summary Report(s) in one step.
              </p>
              <ul className="spirits-review-list">
                <li>
                  Plant #: <strong>{siteId}</strong>
                </li>
                <li>
                  Dump/Batch ID: <strong>{dumpBatchId || '(missing)'}</strong>
                </li>
                <li>
                  Product: {product?.name} · {actuals.bottlesFilled || bottles} bottles
                </li>
                <li>
                  Finished goods:{' '}
                  <strong>{alreadySold ? 'not added (already sold)' : 'bottles added to stock'}</strong>
                </li>
                <li>
                  Lot / producer from source:{' '}
                  {sources
                    .filter((s) => selectedSourceIds.some((id) => sameSpiritId(id, s.id)))
                    .map((s) => `${s.serial} (${s.producerPlantNumber})`)
                    .join(', ')}
                </li>
              </ul>
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="spirit-already-sold"
                  data-testid="bottling-already-sold"
                  checked={alreadySold}
                  onChange={(e) => setAlreadySold(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="spirit-already-sold">
                  Past bottling — bottles already sold. Take the spirit out of the container and print the
                  records, but don&apos;t add bottles to finished goods.
                </label>
              </div>
              <div className="spirits-wizard-nav">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setStep(4)}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  data-testid="save-bottling"
                  onClick={async () => {
                    try {
                      setError(null);
                      await saveBottling();
                    } catch (e: any) {
                      setError(e.message);
                    }
                  }}
                >
                  Save bottling
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {mode === 'transfer' && (
        <div className="no-print spirits-subscreen">
          <p className="text-muted">Bulk only between Madison and Athens. Producer stays on the original DSP.</p>
          <div className="spirits-two-col">
          <div>
            <h4 className="h6">Ship from this distillery</h4>
            <div className="spirits-field">
              <label>Source container</label>
              <select
                className="form-select"
                value={ship.fromContainerId}
                onChange={(e) => setShip({ ...ship, fromContainerId: e.target.value })}
              >
                <option value="">Select…</option>
                {transferableContainers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.containerSerial} — {c.kind} — {Number(c.remainingProofGallons).toFixed(2)} PG
                  </option>
                ))}
              </select>
            </div>
            <div className="spirits-field">
              <label>Proof gallons to ship</label>
              <input
                className="form-control"
                value={ship.proofGallons}
                onChange={(e) => setShip({ ...ship, proofGallons: e.target.value })}
              />
            </div>
            <div className="spirits-field">
              <label>Transfer / invoice #</label>
              <input
                className="form-control"
                value={ship.transferNumber}
                onChange={(e) => setShip({ ...ship, transferNumber: e.target.value })}
                placeholder="e.g. INV #260526"
              />
            </div>
            <div className="spirits-field">
              <label>Date</label>
              <input
                type="date"
                className="form-control"
                value={ship.transferredAt}
                onChange={(e) => setShip({ ...ship, transferredAt: e.target.value })}
              />
            </div>
            <div className="spirits-field">
              <label>Destination DSP</label>
              <select
                className="form-select"
                value={ship.toSiteId}
                onChange={(e) => setShip({ ...ship, toSiteId: e.target.value })}
              >
                {['DSP-AL-20010', 'DSP-AL-20051']
                  .filter((id) => id !== siteId)
                  .map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
              </select>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={async () => {
                try {
                  const result = await postJson('/api/liquor/transfers/ship', {
                    fromSiteId: siteId,
                    toSiteId: ship.toSiteId,
                    fromContainerId: Number(ship.fromContainerId),
                    proofGallons: Number(ship.proofGallons),
                    transferNumber: ship.transferNumber,
                    transferredAt: ship.transferredAt,
                  });
                  setPrintDocs([result.shipReport]);
                  setReceive({
                    transferId: String(result.transfer.id),
                    toContainerSerial: result.transfer.fromContainerSerial,
                    proofGallonsReceived: String(result.transfer.proofGallonsShipped),
                    receivedAt: centralToday(),
                  });
                  setMode('print');
                  await loadBase();
                } catch (e: any) {
                  setError(e.message);
                }
              }}
            >
              Ship &amp; print tank report
            </button>
          </div>
          <div>
            <h4 className="h6">Receive at this distillery</h4>
            <div className="spirits-field">
              <label>Transfer ID</label>
              <input
                className="form-control"
                value={receive.transferId}
                onChange={(e) => setReceive({ ...receive, transferId: e.target.value })}
              />
            </div>
            <div className="spirits-field">
              <label>Receiving container serial #</label>
              <input
                className="form-control"
                value={receive.toContainerSerial}
                onChange={(e) => setReceive({ ...receive, toContainerSerial: e.target.value })}
              />
            </div>
            <div className="spirits-field">
              <label>Proof gallons received</label>
              <input
                className="form-control"
                value={receive.proofGallonsReceived}
                onChange={(e) => setReceive({ ...receive, proofGallonsReceived: e.target.value })}
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={async () => {
                try {
                  const result = await postJson('/api/liquor/transfers/receive', {
                    transferId: Number(receive.transferId),
                    toSiteId: siteId,
                    toContainerSerial: receive.toContainerSerial,
                    proofGallonsReceived: receive.proofGallonsReceived
                      ? Number(receive.proofGallonsReceived)
                      : undefined,
                    receivedAt: receive.receivedAt,
                  });
                  setPrintDocs([result.receiveReport]);
                  setMode('print');
                  await loadBase();
                } catch (e: any) {
                  setError(e.message);
                }
              }}
            >
              Receive &amp; print tank report
            </button>
          </div>
          </div>
        </div>
      )}

      {mode === 'containers' && (
        <div className="no-print spirits-subscreen">
          <p className="text-muted">Setup only — day-to-day work is Bottling and Bond-to-bond transfer.</p>
          {pendingReturn?.mode === 'bottle' && (
            <div className="spirits-return-banner" role="status">
              Receiving for bottling. After you save, you&apos;ll return to the Source step.
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary ms-2"
                onClick={async () => {
                  await finishPrintOrDone();
                }}
              >
                Back to bottling
              </button>
            </div>
          )}
          <div className="spirits-two-col">
            <div>
              <h4 className="h6">Receive a bulk drum</h4>
              {(
                [
                  ['serial', 'Drum serial #'],
                  ['kind', 'Kind of spirits'],
                  ['proof', 'Proof'],
                  ['proofGallons', 'Proof gallons (from paperwork)'],
                  ['tareLb', 'Tare (lb)'],
                  ['currentGrossLb', 'Current gross weight on scale (lb)'],
                  ['vendorName', 'Vendor name'],
                  ['producerPlantNumber', 'Producer DSP #'],
                  ['receivedAt', 'Date received'],
                ] as const
              ).map(([k, label]) => (
                <div className="spirits-field" key={k}>
                  <label>{label}</label>
                  <input
                    className="form-control"
                    type={k === 'receivedAt' ? 'date' : 'text'}
                    value={(drumForm as any)[k]}
                    onChange={(e) => setDrumForm({ ...drumForm, [k]: e.target.value })}
                  />
                </div>
              ))}
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={async () => {
                  try {
                    const result = await postJson('/api/liquor/drums/receive', {
                      siteId,
                      containerSerial: drumForm.serial,
                      kind: drumForm.kind,
                      proof: Number(drumForm.proof),
                      proofGallons: Number(drumForm.proofGallons),
                      tareLb: Number(drumForm.tareLb),
                      currentGrossLb: drumForm.currentGrossLb ? Number(drumForm.currentGrossLb) : undefined,
                      vendorName: drumForm.vendorName,
                      producerPlantNumber: drumForm.producerPlantNumber,
                      receivedAt: drumForm.receivedAt,
                    });
                    setPrintDocs([result.tankSummaryReport]);
                    setMode('print');
                    await loadBase();
                  } catch (e: any) {
                    setError(e.message);
                  }
                }}
              >
                Receive drum
              </button>
            </div>
            <div>
              <h4 className="h6">Opening barrel balance</h4>
              {(
                [
                  ['serial', 'Barrel serial #'],
                  ['kind', 'Kind'],
                  ['capacityGal', 'Capacity (gal)'],
                  ['entryProof', 'Entry proof'],
                  ['fillDate', 'Fill date'],
                  ['producerPlantNumber', 'Producer DSP #'],
                ] as const
              ).map(([k, label]) => (
                <div className="spirits-field" key={k}>
                  <label>{label}</label>
                  <input
                    className="form-control"
                    type={k === 'fillDate' ? 'date' : 'text'}
                    value={(barrelForm as any)[k]}
                    onChange={(e) => setBarrelForm({ ...barrelForm, [k]: e.target.value })}
                  />
                </div>
              ))}
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={async () => {
                  try {
                    const result = await postJson('/api/liquor/barrels/receive', {
                      siteId,
                      containerSerial: barrelForm.serial,
                      kind: barrelForm.kind,
                      capacityGal: Number(barrelForm.capacityGal),
                      entryProof: Number(barrelForm.entryProof),
                      fillDate: barrelForm.fillDate,
                      producerPlantNumber: barrelForm.producerPlantNumber,
                    });
                    setPrintDocs([result.tankSummaryReport]);
                    setMode('print');
                    await loadBase();
                  } catch (e: any) {
                    setError(e.message);
                  }
                }}
              >
                Enter barrel
              </button>
            </div>
          </div>
        </div>
      )}

      {mode === 'products' && (
        <div className="no-print spirits-subscreen">
          <p className="text-muted spirits-products-intro">
            Dump/Batch ID = code + bottling date (ddmmyy). Codes are 2–5 letters. Leave blank if not set yet.
          </p>
          <AddProductForm
            onCreated={async () => {
              await loadBase();
            }}
            onError={setError}
          />
          <div className="spirits-product-list">
            {products.map((p) => (
              <ProductCodeRow
                key={p.id}
                product={p}
                onSaved={async () => {
                  await loadBase();
                }}
                onError={setError}
              />
            ))}
          </div>
        </div>
      )}

      {mode === 'history' && (
        <div className="no-print spirits-subscreen" data-testid="spirits-history">
          {historyContainerId != null && (
            <div className="spirits-return-banner" role="status" data-testid="spirits-history-container-filter">
              History for container <strong>{historySerial}</strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary ms-2"
                onClick={() => {
                  setHistoryContainerId(null);
                  setContainerHistory(null);
                }}
              >
                Show all at this distillery
              </button>
            </div>
          )}

          {!historyView && (
            <p className="text-muted" data-testid="spirits-history-loading">
              Loading history…
            </p>
          )}

          {historyView && historyContainerId != null && (
            <>
              <h4 className="h6 mt-1">Receipts &amp; tank reports</h4>
              <ul className="spirits-history-list list-unstyled">
                {(historyView.tankSummaryReports || [])
                  .filter((t: any) => String(t.relatedRecordType || '') === 'Receipt')
                  .map((t: any) => (
                    <li key={`rcpt-${t.id}`} className="spirits-history-card">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 spirits-history-link"
                        onClick={async () => {
                          const tr = await apiFetch(`${API_BASE_URL}/api/liquor/tank-summary-reports/${t.id}`);
                          setPrintDocs([await tr.json()]);
                          setMode('print');
                        }}
                      >
                        {t.reportDate} Receipt · {Number(t.proofGallonsDelta).toFixed(2)} PG · serial {t.reportSerial}
                      </button>
                    </li>
                  ))}
                {!(historyView.tankSummaryReports || []).some(
                  (t: any) => String(t.relatedRecordType || '') === 'Receipt'
                ) && <li className="text-muted">No receipts on file.</li>}
              </ul>

              <h4 className="h6 mt-3">Withdrawals</h4>
              <ul className="spirits-history-list list-unstyled">
                {(historyView.pulls || []).map((p: any) => (
                  <li key={`pull-${p.id}`} className="spirits-history-card">
                    <div className="spirits-history-link">
                      {p.pulledAt} Pull · {Number(p.proofGallons).toFixed(2)} PG
                      {p.purpose ? ` · ${p.purpose}` : ''}
                      {p.bottlingBatchId ? ` · batch #${p.bottlingBatchId}` : ''}
                    </div>
                    {p.tankSummaryReportId != null && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={async () => {
                          const tr = await apiFetch(
                            `${API_BASE_URL}/api/liquor/tank-summary-reports/${p.tankSummaryReportId}`
                          );
                          setPrintDocs([await tr.json()]);
                          setMode('print');
                        }}
                      >
                        Print report
                      </button>
                    )}
                  </li>
                ))}
                {!(historyView.pulls || []).length && <li className="text-muted">No withdrawals yet.</li>}
              </ul>
            </>
          )}

          {historyView && (
            <>
          <h4 className="h6 mt-1">Bottlings</h4>
          <ul className="spirits-history-list list-unstyled">
            {(historyView.bottlings || []).map((b: any) => (
              <li key={b.id} className="spirits-history-card">
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 spirits-history-link"
                  onClick={async () => {
                    const res = await apiFetch(`${API_BASE_URL}/api/liquor/bottling/${b.id}/dump-sheet`);
                    const sheet = await res.json();
                    const reports = [];
                    for (const reportId of bottlingTankSummaryReportIds(b, historyView.tankSummaryReports)) {
                      const tr = await apiFetch(`${API_BASE_URL}/api/liquor/tank-summary-reports/${reportId}`);
                      if (tr.ok) reports.push(await tr.json());
                    }
                    setPrintDocs([sheet, ...reports]);
                    setEditingBatchId(b.id);
                    setMode('print');
                  }}
                >
                  {b.bottledAt} {b.dumpBatchId} — {b.bottlesFilled} btls
                  {Number(b.finishedGoodsAdded) === 0 ? ' (already sold)' : ''} [{b.status}]
                  {b.editCount ? ` · edited ${b.editCount}×` : ''}
                </button>
                {b.status !== 'Voided' && (
                  <div className="spirits-history-actions">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      data-testid={`edit-bottling-${b.id}`}
                      onClick={() => openBottlingEdit(b.id)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={async () => {
                        const reason = window.prompt('Reason for voiding this bottling?') || '';
                        if (!reason.trim()) return;
                        try {
                          await postJson(`/api/liquor/bottling/${b.id}/void`, { reason });
                          await loadBase();
                          if (historyContainerId != null) {
                            const res = await apiFetch(
                              `${API_BASE_URL}/api/liquor/containers/${historyContainerId}/history`
                            );
                            if (res.ok) setContainerHistory(await res.json());
                          }
                        } catch (e: any) {
                          setError(e.message);
                        }
                      }}
                    >
                      Void
                    </button>
                  </div>
                )}
              </li>
            ))}
            {!(historyView.bottlings || []).length && <li className="text-muted">No bottlings yet.</li>}
          </ul>
          <h4 className="h6 mt-3">Transfers</h4>
          <ul className="spirits-history-list list-unstyled">
            {(historyView.transfers || []).map((t: any) => (
              <li key={t.id} className="spirits-history-card">
                <div className="spirits-history-link">
                  {t.transferredAt} {t.transferNumber}: {t.fromSiteId} → {t.toSiteId} {t.proofGallonsShipped} PG [
                  {t.status}]
                </div>
                {t.status !== 'Voided' && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={async () => {
                      const reason = window.prompt('Reason for voiding this transfer?') || '';
                      try {
                        await postJson(`/api/liquor/transfers/${t.id}/void`, { reason });
                        await loadBase();
                        if (historyContainerId != null) {
                          const res = await apiFetch(
                            `${API_BASE_URL}/api/liquor/containers/${historyContainerId}/history`
                          );
                          if (res.ok) setContainerHistory(await res.json());
                        }
                      } catch (e: any) {
                        setError(e.message);
                      }
                    }}
                  >
                    Void
                  </button>
                )}
              </li>
            ))}
            {!(historyView.transfers || []).length && <li className="text-muted">No transfers yet.</li>}
          </ul>
            </>
          )}
        </div>
      )}

      {mode === 'edit' && editingBatchId != null && (
        <BottlingEditForm
          batchId={editingBatchId}
          products={products}
          patchJson={patchJson}
          postJson={postJson}
          onError={setError}
          onSaved={async (result) => {
            const docs = [result.dumpBatchSheet, ...(result.tankSummaryReports || [])];
            setPrintDocs(docs);
            setMode('print');
            await loadBase();
          }}
          onVoided={async () => {
            setEditingBatchId(null);
            goToMode('history');
            await loadBase();
          }}
          onCancel={() => {
            setEditingBatchId(null);
            goToMode('history');
          }}
        />
      )}

      {mode === 'edit' && editingDrumId != null && editingBatchId == null && (
        <DrumReceiptEditForm
          drumId={editingDrumId}
          containers={containers}
          patchJson={patchJson}
          onError={setError}
          onSaved={async (result) => {
            setPrintDocs([result.tankSummaryReport]);
            setEditingDrumId(null);
            setMode('print');
            await loadBase();
          }}
          onCancel={() => {
            setEditingDrumId(null);
            goHome();
          }}
        />
      )}

      {mode === 'print' && printDocs.length > 0 && (
        <div>
          <div className="no-print spirits-wizard-nav mb-3">
            <button type="button" className="btn btn-primary" onClick={() => window.print()}>
              Print
            </button>
            {editingBatchId != null &&
              printDocs.some((d) => d.dumpBatchId || d.title === 'PROCESSING ACCOUNT DUMP/BATCH SHEET') && (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  data-testid="edit-from-print"
                  onClick={() => openBottlingEdit(editingBatchId)}
                >
                  Edit bottling
                </button>
              )}
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                finishPrintOrDone().catch((e) => setError(e.message));
              }}
            >
              {pendingReturn ? 'Done — back to bottling' : 'Done'}
            </button>
          </div>
          {printDocs.map((doc, i) =>
            doc.title === 'PROCESSING ACCOUNT DUMP/BATCH SHEET' || doc.dumpBatchId ? (
              <DumpSheet key={i} doc={doc} />
            ) : (
              <TankSummary key={i} doc={doc} />
            )
          )}
        </div>
      )}
    </div>
  );
};

function BottlingEditForm({
  batchId,
  products,
  patchJson,
  postJson,
  onError,
  onSaved,
  onVoided,
  onCancel,
}: {
  batchId: number;
  products: Product[];
  patchJson: (path: string, body: object) => Promise<any>;
  postJson: (path: string, body: object) => Promise<any>;
  onError: (m: string) => void;
  onSaved: (result: any) => Promise<void>;
  onVoided: () => Promise<void>;
  onCancel: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState<any[]>([]);
  const [form, setForm] = useState({
    bottledAt: '',
    productId: '',
    productCode: '',
    typeOfSpirits: '',
    labelingProof: '',
    bottleSizeMl: '750',
    bottlesFilled: '',
    dumpBatchId: '',
    lotOfSpirit: '',
    proofGallons: '',
    actualWaterPounds: '',
    alreadySold: false,
    reason: '',
    notes: '',
  });
  const [loadedActualWater, setLoadedActualWater] = useState('');
  const [plannedWater, setPlannedWater] = useState<number | null>(null);
  const [inputId, setInputId] = useState<number | null>(null);
  const [status, setStatus] = useState('Finalized');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/liquor/bottling/${batchId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load bottling');
        if (cancelled) return;
        const b = data.batch;
        const inp = (data.inputs && data.inputs[0]) || {};
        setStatus(b.status);
        setInputId(inp.id != null ? Number(inp.id) : null);
        setEdits(data.edits || []);
        setLoadedActualWater(b.actualWaterPounds != null ? String(b.actualWaterPounds) : '');
        setPlannedWater(b.plannedWaterPounds != null ? Number(b.plannedWaterPounds) : null);
        setForm({
          bottledAt: String(b.bottledAt || '').slice(0, 10),
          productId: b.productId != null ? String(b.productId) : '',
          productCode: b.productCode || '',
          typeOfSpirits: b.typeOfSpirits || '',
          labelingProof: String(b.labelingProof ?? ''),
          bottleSizeMl: String(b.bottleSizeMl || 750),
          bottlesFilled: String(b.bottlesFilled ?? ''),
          dumpBatchId: b.dumpBatchId || '',
          lotOfSpirit: b.lotOfSpirit || '',
          proofGallons: String(inp.proofGallons ?? b.proofGallonsIn ?? ''),
          actualWaterPounds: b.actualWaterPounds != null ? String(b.actualWaterPounds) : '',
          alreadySold: Number(b.finishedGoodsAdded) === 0,
          reason: '',
          notes: b.notes || '',
        });
      } catch (e: any) {
        onError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [batchId, onError]);

  if (loading) {
    return (
      <div className="no-print spirits-subscreen" data-testid="bottling-edit-loading">
        Loading bottling…
      </div>
    );
  }

  if (status === 'Voided') {
    return (
      <div className="no-print spirits-subscreen">
        <p className="text-muted">This bottling is voided and cannot be edited.</p>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="no-print spirits-subscreen" data-testid="bottling-edit-form">
      <p className="text-muted">
        Correct this bottling. Container balances, later running remainings, the daily summary, tank
        summary report, dump/batch sheet, and finished goods stay in sync. A reason is required for
        the audit trail.
      </p>
      <div className="spirits-field">
        <label>Date</label>
        <input
          className="form-control"
          type="date"
          value={form.bottledAt}
          onChange={(e) => setForm({ ...form, bottledAt: e.target.value })}
          data-testid="edit-bottled-at"
        />
      </div>
      <div className="spirits-field">
        <label>Product</label>
        <select
          className="form-select"
          value={form.productId}
          onChange={(e) => {
            const p = products.find((x) => String(x.id) === e.target.value);
            setForm({
              ...form,
              productId: e.target.value,
              productCode: p?.code || form.productCode,
              typeOfSpirits: p?.kind || form.typeOfSpirits,
              labelingProof: p ? String(p.targetProof) : form.labelingProof,
              bottleSizeMl: p ? String(p.bottleSizeMl || 750) : form.bottleSizeMl,
            });
          }}
          data-testid="edit-product"
        >
          <option value="">Keep current ({form.productCode || form.typeOfSpirits})</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.code})
            </option>
          ))}
        </select>
      </div>
      <div className="spirits-field">
        <label>Dump / batch number</label>
        <input
          className="form-control"
          value={form.dumpBatchId}
          onChange={(e) => setForm({ ...form, dumpBatchId: e.target.value.toUpperCase() })}
          data-testid="edit-dump-batch-id"
        />
      </div>
      <div className="spirits-field">
        <label>Lot # of spirit</label>
        <input
          className="form-control"
          value={form.lotOfSpirit}
          onChange={(e) => setForm({ ...form, lotOfSpirit: e.target.value })}
        />
      </div>
      <div className="spirits-field">
        <label>Labeling proof</label>
        <input
          className="form-control"
          value={form.labelingProof}
          onChange={(e) => setForm({ ...form, labelingProof: e.target.value })}
          data-testid="edit-labeling-proof"
        />
      </div>
      <div className="spirits-field">
        <label>Bottle size (ml)</label>
        <select
          className="form-select"
          value={form.bottleSizeMl}
          onChange={(e) => setForm({ ...form, bottleSizeMl: e.target.value })}
        >
          <option value="750">750</option>
          <option value="200">200</option>
        </select>
      </div>
      <div className="spirits-field">
        <label>Bottles filled</label>
        <input
          className="form-control"
          value={form.bottlesFilled}
          onChange={(e) => setForm({ ...form, bottlesFilled: e.target.value })}
          data-testid="edit-bottles-filled"
        />
      </div>
      <div className="spirits-field">
        <label>Proof gallons withdrawn from container</label>
        <input
          className="form-control"
          value={form.proofGallons}
          onChange={(e) => setForm({ ...form, proofGallons: e.target.value })}
          data-testid="edit-proof-gallons"
        />
      </div>
      <div className="spirits-field">
        <label>Actual water added (lb)</label>
        <input
          className="form-control"
          inputMode="decimal"
          value={form.actualWaterPounds}
          onChange={(e) => setForm({ ...form, actualWaterPounds: e.target.value })}
          data-testid="edit-actual-water-pounds"
        />
        <span className="hint">
          {plannedWater != null ? `Planned: ${plannedWater} lb. ` : ''}
          What the scale showed. Water gallons and total weight are recalculated.
        </span>
      </div>
      <label className="spirits-check">
        <input
          type="checkbox"
          checked={form.alreadySold}
          onChange={(e) => setForm({ ...form, alreadySold: e.target.checked })}
          data-testid="edit-already-sold"
        />{' '}
        Past bottling — bottles already sold (do not add to finished goods)
      </label>
      <div className="spirits-field">
        <label>Notes</label>
        <input
          className="form-control"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </div>
      <div className="spirits-field">
        <label>Reason for this correction</label>
        <input
          className="form-control"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          required
          data-testid="edit-reason"
          placeholder="Required — kept in the audit trail"
        />
      </div>
      <div className="spirits-wizard-nav">
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          data-testid="save-bottling-edit"
          disabled={busy || !form.reason.trim()}
          onClick={async () => {
            setBusy(true);
            try {
              const body: any = {
                bottledAt: form.bottledAt,
                productId: form.productId ? Number(form.productId) : undefined,
                productCode: form.productCode || undefined,
                typeOfSpirits: form.typeOfSpirits,
                labelingProof: Number(form.labelingProof),
                bottleSizeMl: Number(form.bottleSizeMl),
                bottlesFilled: Number(form.bottlesFilled),
                dumpBatchId: form.dumpBatchId || undefined,
                lotOfSpirit: form.lotOfSpirit,
                notes: form.notes,
                alreadySold: form.alreadySold,
                addFinishedGoods: !form.alreadySold,
                reason: form.reason.trim(),
                ...(form.actualWaterPounds.trim() !== loadedActualWater
                  ? {
                      actualWaterPounds:
                        form.actualWaterPounds.trim() === '' ? null : Number(form.actualWaterPounds),
                    }
                  : {}),
                inputs: [
                  {
                    id: inputId || undefined,
                    proofGallons: Number(form.proofGallons),
                  },
                ],
              };
              const result = await patchJson(`/api/liquor/bottling/${batchId}`, body);
              await onSaved(result);
            } catch (e: any) {
              onError(e.message);
            } finally {
              setBusy(false);
            }
          }}
        >
          Save correction
        </button>
        <button
          type="button"
          className="btn btn-outline-danger"
          disabled={busy}
          onClick={async () => {
            const reason =
              form.reason.trim() || window.prompt('Reason for voiding this bottling?') || '';
            if (!reason.trim()) return;
            setBusy(true);
            try {
              await postJson(`/api/liquor/bottling/${batchId}/void`, { reason });
              await onVoided();
            } catch (e: any) {
              onError(e.message);
            } finally {
              setBusy(false);
            }
          }}
        >
          Void bottling
        </button>
      </div>
      {edits.length > 0 && (
        <div className="mt-4">
          <h4 className="h6">Correction history</h4>
          <ul className="list-unstyled spirits-edit-history">
            {edits.map((e) => (
              <li key={e.id} className="small text-muted mb-2">
                {e.editedAt} · {e.action}
                {e.reason ? ` — ${e.reason}` : ''}
                {e.oldValues?.proofGallonsIn != null && e.newValues?.proofGallonsIn != null
                  ? ` (PG in ${e.oldValues.proofGallonsIn} → ${e.newValues.proofGallonsIn})`
                  : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function DrumReceiptEditForm({
  drumId,
  containers,
  patchJson,
  onError,
  onSaved,
  onCancel,
}: {
  drumId: number;
  containers: any[];
  patchJson: (path: string, body: object) => Promise<any>;
  onError: (m: string) => void;
  onSaved: (result: any) => Promise<void>;
  onCancel: () => void;
}) {
  const drum = containers.find((c) => String(c.id) === String(drumId));
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    receivedAt: String(drum?.receivedAt || '').slice(0, 10),
    proof: String(drum?.proof ?? ''),
    // Remaining is after withdrawals; receipt PG is not on the container row.
    // Operator enters the corrected original receipt PG.
    proofGallons: '',
    reason: '',
  });

  if (!drum) {
    return (
      <div className="no-print spirits-subscreen">
        <p className="text-muted">Drum not found.</p>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="no-print spirits-subscreen" data-testid="drum-receipt-edit-form">
      <h4 className="h6">Edit receipt — {drum.containerSerial}</h4>
      <p className="text-muted">
        Correct the original receipt quantity, proof, or date. Later bottling remainings on this drum
        are recalculated. Current balance on hand: {Number(drum.remainingProofGallons).toFixed(2)} PG.
      </p>
      <div className="spirits-field">
        <label>Date received</label>
        <input
          className="form-control"
          type="date"
          value={form.receivedAt}
          onChange={(e) => setForm({ ...form, receivedAt: e.target.value })}
        />
      </div>
      <div className="spirits-field">
        <label>Proof</label>
        <input
          className="form-control"
          value={form.proof}
          onChange={(e) => setForm({ ...form, proof: e.target.value })}
        />
      </div>
      <div className="spirits-field">
        <label>Proof gallons on original receipt</label>
        <input
          className="form-control"
          value={form.proofGallons}
          onChange={(e) => setForm({ ...form, proofGallons: e.target.value })}
          data-testid="edit-receipt-pg"
          placeholder="Original paperwork PG"
        />
      </div>
      <div className="spirits-field">
        <label>Reason for this correction</label>
        <input
          className="form-control"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          data-testid="edit-receipt-reason"
          placeholder="Required"
        />
      </div>
      <div className="spirits-wizard-nav">
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || !form.reason.trim() || !form.proofGallons}
          data-testid="save-receipt-edit"
          onClick={async () => {
            setBusy(true);
            try {
              const result = await patchJson(`/api/liquor/drums/${drumId}/receipt`, {
                receivedAt: form.receivedAt,
                proof: Number(form.proof),
                proofGallons: Number(form.proofGallons),
                reason: form.reason.trim(),
              });
              await onSaved(result);
            } catch (e: any) {
              onError(e.message);
            } finally {
              setBusy(false);
            }
          }}
        >
          Save receipt correction
        </button>
      </div>
    </div>
  );
}

function AddProductForm({
  onCreated,
  onError,
}: {
  onCreated: () => Promise<void>;
  onError: (m: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [batchCode, setBatchCode] = useState('');
  const [targetProof, setTargetProof] = useState('80');
  const [bottlesPerCase, setBottlesPerCase] = useState('6');
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <button type="button" className="btn btn-outline-primary btn-sm mb-3" onClick={() => setOpen(true)}>
        Add product
      </button>
    );
  }

  return (
    <form
      className="spirits-product-card spirits-product-add mb-3"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
          const res = await apiFetch(`${API_BASE_URL}/api/liquor/products`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              name: name.trim(),
              batchCode: batchCode.trim() || null,
              targetProof: Number(targetProof) || 80,
              bottlesPerCase: Number(bottlesPerCase) || 6,
            }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Create failed');
          setName('');
          setBatchCode('');
          setTargetProof('80');
          setBottlesPerCase('6');
          setOpen(false);
          await onCreated();
        } catch (err: any) {
          onError(err.message);
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="spirits-product-card-title">New product</div>
      <label className="spirits-field">
        Name
        <input
          className="form-control"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="Product name"
        />
      </label>
      <div className="spirits-product-fields">
        <label className="spirits-field">
          Batch code
          <input
            className="form-control"
            value={batchCode}
            maxLength={5}
            onChange={(e) => setBatchCode(e.target.value.toUpperCase())}
            placeholder="Optional"
          />
        </label>
        <label className="spirits-field">
          Target proof
          <input
            className="form-control"
            type="number"
            min={1}
            value={targetProof}
            onChange={(e) => setTargetProof(e.target.value)}
          />
        </label>
        <label className="spirits-field">
          Bottles / case
          <input
            className="form-control"
            type="number"
            min={1}
            value={bottlesPerCase}
            onChange={(e) => setBottlesPerCase(e.target.value)}
          />
        </label>
      </div>
      <div className="spirits-product-actions">
        <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()}>
          {busy ? 'Saving…' : 'Create'}
        </button>
        <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ProductCodeRow({
  product,
  onSaved,
  onError,
}: {
  product: Product;
  onSaved: () => Promise<void>;
  onError: (m: string) => void;
}) {
  const [name, setName] = useState(product.name);
  const [code, setCode] = useState(product.batchCode || '');
  const [targetProof, setTargetProof] = useState(String(product.targetProof ?? 80));
  const [bottlesPerCase, setBottlesPerCase] = useState(String(product.bottlesPerCase ?? 6));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(product.name);
    setCode(product.batchCode || '');
    setTargetProof(String(product.targetProof ?? 80));
    setBottlesPerCase(String(product.bottlesPerCase ?? 6));
  }, [product.id, product.name, product.batchCode, product.targetProof, product.bottlesPerCase]);

  const needsCode = !product.batchCode;

  return (
    <div className="spirits-product-card">
      <div className="spirits-product-card-head">
        <div className="spirits-product-card-title">{product.name}</div>
        {needsCode && <span className="spirits-needs-code">needs code</span>}
      </div>
      <label className="spirits-field">
        Name
        <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <div className="spirits-product-fields">
        <label className="spirits-field">
          Batch code
          <input
            className="form-control"
            value={code}
            maxLength={5}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. BLD"
          />
        </label>
        <label className="spirits-field">
          Target proof
          <input
            className="form-control"
            type="number"
            min={1}
            value={targetProof}
            onChange={(e) => setTargetProof(e.target.value)}
          />
        </label>
        <label className="spirits-field">
          Bottles / case
          <input
            className="form-control"
            type="number"
            min={1}
            value={bottlesPerCase}
            onChange={(e) => setBottlesPerCase(e.target.value)}
          />
        </label>
      </div>
      <div className="spirits-product-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || !name.trim()}
          onClick={async () => {
            setBusy(true);
            try {
              const res = await apiFetch(`${API_BASE_URL}/api/liquor/products/${product.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                  name: name.trim(),
                  batchCode: code.trim() || null,
                  targetProof: Number(targetProof) || product.targetProof,
                  bottlesPerCase: Number(bottlesPerCase) || product.bottlesPerCase,
                }),
              });
              const data = await res.json();
              if (!res.ok) throw new Error(data.error || 'Save failed');
              await onSaved();
            } catch (e: any) {
              onError(e.message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          className="btn btn-outline-danger"
          disabled={busy}
          onClick={async () => {
            if (!window.confirm(`Deactivate “${product.name}”? It will be hidden from lists.`)) return;
            setBusy(true);
            try {
              const res = await apiFetch(`${API_BASE_URL}/api/liquor/products/${product.id}/deactivate`, {
                method: 'POST',
                headers: { Accept: 'application/json' },
              });
              const data = await res.json();
              if (!res.ok) throw new Error(data.error || 'Deactivate failed');
              await onSaved();
            } catch (e: any) {
              onError(e.message);
            } finally {
              setBusy(false);
            }
          }}
        >
          Deactivate
        </button>
      </div>
    </div>
  );
}

function TankSummary({ doc }: { doc: any }) {
  const voided = doc.status === 'VOID' || doc.voidedAt;
  return (
    <div className={`spirit-print-sheet ${voided ? 'voided' : ''}`} data-testid="tank-summary-report">
      <h1>Tank Summary Report{voided ? ' — VOID' : ''}</h1>
      <div className="spirit-print-grid single">
        {(
          [
            ['Tank/Barrel ID/Serial #', doc.fields?.['Tank/Barrel ID/Serial #'] || doc.containerSerial],
            ['Producer', doc.fields?.Producer || doc.producerPlantNumber],
            [
              'Proof Gallons Withdrawn/Deposited',
              doc.fields?.['Proof Gallons Withdrawn/Deposited'] ?? doc.proofGallonsDelta,
            ],
            ['Type of Spirits', doc.fields?.['Type of Spirits'] || doc.typeOfSpirits],
            ['Proof Gallons Remaining', doc.fields?.['Proof Gallons Remaining'] ?? doc.proofGallonsRemaining],
            ['Date', doc.fields?.Date || doc.reportDate],
            ['Proof', doc.fields?.Proof ?? doc.proof],
            ['Report Serial #', doc.fields?.['Report Serial #'] || doc.reportSerial],
          ] as [string, any][]
        ).map(([label, value]) => (
          <div className="spirit-print-field" key={label}>
            <label>{label}</label>
            <div className="value">{value}</div>
          </div>
        ))}
      </div>
      <p className="spirit-print-perjury">{doc.perjuryDeclaration || PERJURY}</p>
      <div className="spirit-print-signature">Signature</div>
    </div>
  );
}

function DumpSheet({ doc }: { doc: any }) {
  return (
    <div className="spirit-print-sheet" data-testid="dump-batch-sheet">
      <h1>Processing Account Dump/Batch Sheet</h1>
      <div className="spirit-print-grid">
        {(
          [
            ['Plant #', doc.plantNumber || doc.fields?.['Plant #']],
            ['Dump/Batch ID', doc.dumpBatchId || doc.fields?.['Dump/Batch ID']],
            ['Tank Number', doc.fields?.['Tank Number']],
            ['Type of Spirits', doc.fields?.['Type of Spirits']],
            ['Formula #', doc.fields?.['Formula #']],
            ['Date', doc.fields?.Date],
            ['Proof @ Temp', doc.fields?.['Proof @ Temp']],
            ['Producer (origin DSP)', doc.producerPlantNumber],
            ['# of Bottles', doc.fields?.['# of Bottles']],
            ['# of Cases / Case Size', `${doc.fields?.['# of Cases']} / ${doc.fields?.['Case Size']}`],
            [
              'Bottle Size',
              `${doc.fields?.['Bottle Size 750 ML'] ? '☑ 750 ML' : '☐ 750 ML'} ${
                doc.fields?.['Bottle Size 200 ML'] ? '☑ 200 ML' : '☐ 200 ML'
              }`,
            ],
            ['Shrinkage % (info only)', doc.fields?.['Shrinkage %']],
            ['Bulk Quantity spirit gal', doc.fields?.['Bulk Quantity spirit gal']],
            ['Filtered/Distilled Water gal', doc.fields?.['Filtered/Distilled Water gal']],
            ['Bulk Weight spirit lbs', doc.fields?.['Bulk Weight spirit lbs']],
            ['Water lbs', doc.fields?.['Filtered/Distilled Water lbs']],
            ['Planned Water lbs', doc.fields?.['Planned Water lbs']],
            ['Actual Water lbs', doc.fields?.['Actual Water lbs']],
            ['Total Weight', doc.fields?.['Total Weight']],
            ['Proof Gallons', doc.fields?.['Proof Gallons']],
            ['Lot # of Spirit', doc.fields?.['Lot # of Spirit']],
            ['Serial #', doc.fields?.['Serial #']],
          ] as [string, any][]
        ).map(([label, value]) => (
          <div className="spirit-print-field" key={label}>
            <label>{label}</label>
            <div className="value">{value}</div>
          </div>
        ))}
      </div>
      <p className="spirit-print-note">
        {doc.shrinkNote ||
          '*Water per 27 CFR 30.64 (TTB Gauging Manual Tables 4 & 5); shrinkage % is for information only'}
      </p>
      <p className="spirit-print-perjury">{doc.perjuryDeclaration || PERJURY}</p>
      <div className="spirit-print-signature">Signature</div>
    </div>
  );
}

export default SpiritsDashboard;
